'use client';
import { useState, useRef, useEffect } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { Search, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { orderSearchParams } from '@/utils/search/params';

const SearchInput = ({ isApiSelect }: { isApiSelect?: boolean }) => {
  const searchParams = useSearchParams();
  const t = useTranslations('search');
  const ssrSearch = searchParams.get('q') || '';
  const [search, setSearch] = useState(ssrSearch);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const pathname = usePathname();

  const rootPathname = isApiSelect ? '/api/v1/select' : '/search';

  useEffect(() => {
    if (pathname !== rootPathname) {
      setSearch('');
    }
    if (ssrSearch !== search) {
      setSearch(ssrSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, ssrSearch]);

  const hrefWithQuery = (nextQuery: string | null) => {
    const params = new URLSearchParams(searchParams.toString());
    if (nextQuery) {
      params.set('q', nextQuery);
    } else {
      params.delete('q');
    }
    const qs = orderSearchParams(params);
    return qs ? `${rootPathname}?${qs}` : rootPathname;
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Empty/whitespace commits a cleared query (removes `q`) rather than no-op.
      router.push(hrefWithQuery(search.trim() || null));
      inputRef.current?.blur();
    }
  };

  const handleClear = () => {
    setSearch('');
    if (ssrSearch) {
      router.push(hrefWithQuery(null));
    }
    inputRef.current?.focus();
  };

  return (
    <div className="w-full md:flex-1 relative flex">
      <input
        placeholder={t('searchPlaceholder')}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={handleKeyPress}
        ref={inputRef}
        value={search}
        enterKeyHint="search"
        className="w-full bg-zinc-900 outline-none rounded-full border border-zinc-700 pl-10 pr-10 h-[48px] flex-1"
      />
      <Search className="w-4 h-4 text-zinc-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
      {search.length > 0 && (
        <button
          onClick={handleClear}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-300"
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default SearchInput;
